import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/core/auth/auth';
import { Loader2, Clock, XCircle, AlertTriangle, RefreshCw, LogOut, Trash2, CreditCard, Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { buildCallbackUrl } from '@/core/auth/redirect-utils';

interface OnboardingGateProps {
  children: ReactNode;
}

/**
 * OnboardingGate Component
 *
 * Security boundary that prevents access to clinical features until the user
 * has completed onboarding AND been approved by an admin.
 *
 * Uses the auth state machine to determine access:
 * - BOOTING → Show loading spinner with timeout recovery
 * - UNAUTHENTICATED → Redirect to login
 * - GATED → Show appropriate gate screen based on gatingReason
 * - AUTHENTICATED → Render children
 * - ERROR → Show recovery UI
 */
export function OnboardingGate({ children }: OnboardingGateProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const {
    authState,
    gatingReason,
    user,
    error,
    signOut,
    clearError,
  } = useAuth();

  // Resend verification cooldown (60s) to avoid hammering Supabase
  const [resendCooldownUntil, setResendCooldownUntil] = useState<number>(0);
  const [resending, setResending] = useState(false);
  const resendSecondsLeft = Math.max(0, Math.ceil((resendCooldownUntil - Date.now()) / 1000));

  // Force a re-render every second while cooldown is active so the button label counts down
  useEffect(() => {
    if (resendCooldownUntil <= Date.now()) return;
    const interval = setInterval(() => {
      if (Date.now() >= resendCooldownUntil) {
        clearInterval(interval);
      }
      // Trigger re-render
      setResendCooldownUntil((prev) => prev);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldownUntil]);

  const handleResendVerification = async () => {
    if (!user?.email || resending || resendSecondsLeft > 0) return;
    setResending(true);
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
        options: { emailRedirectTo: buildCallbackUrl() },
      });
      if (resendError) {
        toast({
          variant: 'destructive',
          title: 'Could Not Resend Email',
          description: resendError.message || 'Please try again in a moment.',
        });
      } else {
        toast({
          title: 'Verification Email Sent',
          description: `We sent a fresh verification link to ${user.email}.`,
        });
        setResendCooldownUntil(Date.now() + 60_000);
      }
    } finally {
      setResending(false);
    }
  };

  // Redirect unauthenticated users to login
  useEffect(() => {
    if (authState === 'UNAUTHENTICATED') {
      navigate('/login', { replace: true });
    }
  }, [authState, navigate]);

  // Redirect users who need onboarding
  useEffect(() => {
    if (authState === 'GATED' && gatingReason === 'ONBOARDING_REQUIRED') {
      if (!location.pathname.startsWith('/app/onboarding')) {
        navigate('/app/onboarding', { replace: true });
      }
    }
  }, [authState, gatingReason, navigate, location.pathname]);

  // Handle session clear for stuck states
  const handleClearSession = () => {
    // Clear localStorage auth data
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('sb-')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    window.location.reload();
  };

  const handleRetry = () => {
    clearError();
    window.location.reload();
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  // -----------------------------------------------------------------------------
  // BOOTING STATE
  // -----------------------------------------------------------------------------
  if (authState === 'BOOTING') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------------
  // ERROR STATE - Recovery UI
  // -----------------------------------------------------------------------------
  if (authState === 'ERROR') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Session Error</CardTitle>
            <CardDescription>
              {error || 'Unable to establish a secure session'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              This may be caused by browser settings, network issues, or an expired session.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={handleRetry} className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
              <Button variant="outline" onClick={handleSignOut} className="w-full">
                <LogOut className="mr-2 h-4 w-4" />
                Return to Login
              </Button>
              <Button variant="ghost" onClick={handleClearSession} className="w-full text-muted-foreground">
                <Trash2 className="mr-2 h-4 w-4" />
                Clear Session Data
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // -----------------------------------------------------------------------------
  // UNAUTHENTICATED STATE
  // -----------------------------------------------------------------------------
  if (authState === 'UNAUTHENTICATED') {
    return null; // Redirect will happen via useEffect
  }

  // -----------------------------------------------------------------------------
  // GATED STATE - Various blocking screens
  // -----------------------------------------------------------------------------
  if (authState === 'GATED') {
    // Onboarding required - redirect handled by useEffect
    if (gatingReason === 'ONBOARDING_REQUIRED') {
      return null;
    }

    // Pending approval
    if (gatingReason === 'PENDING_APPROVAL') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <Clock className="h-6 w-6 text-slate-600 dark:text-slate-400" />
              </div>
              <CardTitle>Pending Approval</CardTitle>
              <CardDescription>
                Your account is awaiting admin approval
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Thank you for completing the onboarding process. Your application is being
                reviewed by our team. You'll receive access once approved.
              </p>
              <p className="text-xs text-muted-foreground">
                This typically takes 1-2 business days. You'll be notified when your
                account is activated.
              </p>
              <div className="flex flex-col gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Check Status
                </Button>
                <Button variant="ghost" onClick={handleSignOut} className="text-muted-foreground">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Rejected
    if (gatingReason === 'REJECTED') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <XCircle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle>Application Not Approved</CardTitle>
              <CardDescription>
                Your account application was not approved
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Unfortunately, we were unable to approve your application at this time.
                If you believe this is an error, please contact our support team.
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  onClick={() => window.location.href = 'mailto:support@radiologylive.ai'}
                >
                  Contact Support
                </Button>
                <Button variant="ghost" onClick={handleSignOut} className="text-muted-foreground">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Suspended
    if (gatingReason === 'SUSPENDED') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle>Account Suspended</CardTitle>
              <CardDescription>
                Your account has been temporarily suspended
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Your account access has been suspended. Please contact our support team
                for more information.
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  onClick={() => window.location.href = 'mailto:support@radiologylive.ai'}
                >
                  Contact Support
                </Button>
                <Button variant="ghost" onClick={handleSignOut} className="text-muted-foreground">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Expired (trial) — route to billing
    if (gatingReason === 'EXPIRED') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <Clock className="h-6 w-6 text-slate-600 dark:text-slate-400" />
              </div>
              <CardTitle>Subscription Required</CardTitle>
              <CardDescription>
                Upgrade to continue using RadiologyLive.ai
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Your trial has ended. Choose a plan to continue with unlimited AI-powered reporting.
              </p>
              <div className="flex flex-col gap-2">
                <Button onClick={() => navigate('/app/billing/plans')}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  View Plans
                </Button>
                <Button variant="ghost" onClick={handleSignOut} className="text-muted-foreground">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Unverified email
    if (gatingReason === 'UNVERIFIED') {
      const resendDisabled = resending || resendSecondsLeft > 0 || !user?.email;
      const resendLabel = resending
        ? 'Sending...'
        : resendSecondsLeft > 0
          ? `Resend available in ${resendSecondsLeft}s`
          : 'Resend Verification Email';

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <Mail className="h-6 w-6 text-slate-600 dark:text-slate-400" />
              </div>
              <CardTitle>Email Verification Required</CardTitle>
              <CardDescription>
                {user?.email ? (
                  <>We sent a verification link to <span className="font-medium">{user.email}</span></>
                ) : (
                  'Please verify your email address to continue'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Check your inbox (and spam folder) for the link. Click it to activate
                your account, then return here.
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  I've Verified My Email
                </Button>
                <Button
                  variant="outline"
                  onClick={handleResendVerification}
                  disabled={resendDisabled}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  {resendLabel}
                </Button>
                <Button variant="ghost" onClick={handleSignOut} className="text-muted-foreground">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Fallback for unknown gating reasons
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CardTitle>Access Restricted</CardTitle>
            <CardDescription>
              Unable to verify access permissions
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Your account doesn't have access to clinical features at this time.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => window.location.reload()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
              <Button variant="ghost" onClick={handleSignOut} className="text-muted-foreground">
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // -----------------------------------------------------------------------------
  // TEMPORARY: Bypass all gating for development/testing
  // TODO: Re-enable gating when onboarding flow is stable
  // -----------------------------------------------------------------------------
  if (authState !== 'BOOTING' && authState !== 'UNAUTHENTICATED') {
    return <>{children}</>;
  }

  // -----------------------------------------------------------------------------
  // AUTHENTICATED STATE - Render children
  // -----------------------------------------------------------------------------
  if (authState === 'AUTHENTICATED') {
    return <>{children}</>;
  }

  // Fallback - should never reach here
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <CardTitle>Unexpected State</CardTitle>
          <CardDescription>
            Something went wrong
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh Page
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
